// 会社情報確認・編集ページ

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppNavigation } from '../utils/navigation';
import type { BCPNavigationState } from '../types/navigation';
import Alert from '../components/Alert';
import BCPGuideSection from '../components/BCPGuideSection';
import BCPStepIndicator from '../components/BCPStepIndicator';
import { buildCompanyInfoFromUpload } from '../utils/companyInfoMapper';
import { logger } from '../utils/logger';
import { generateCriticalBusiness } from '../services/criticalBusinessApi';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import '../styles/placeholder.css';

type CompanyInfo = NonNullable<BCPNavigationState['companyInfo']>;

const CompanyInfoPage: React.FC = () => {
  const location = useLocation();
  const navigation = useAppNavigation();
  const state = location.state as BCPNavigationState;

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    companyName: '',
    location: '',
    employeeCount: undefined,
    businessDescription: '',
    criticalBusiness: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);

  // 離脱確認ダイアログ(新規BCP作成時は常に表示)
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;

      // 新規BCP作成時は、編集していなくても離脱時に確認ダイアログを表示
      // (CompanyInfoPageは常に新規BCP作成フロー)
      return isGenerating;
    },
  });

  // ページ遷移時にスクロール位置を最上部にリセット
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // location.stateから抽出結果を読み込む
    if (state?.companyInfo) {
      // companyInfoが存在する場合はそのまま使用
      setCompanyInfo({
        companyName: state.companyInfo.companyName || '',
        location: state.companyInfo.location || '',
        employeeCount: state.companyInfo.employeeCount || undefined,
        businessDescription: state.companyInfo.businessDescription || '',
        criticalBusiness: state.companyInfo.criticalBusiness || '重要事業1： \n重要事業2：',
      });

      logger.debug('Initialized from companyInfo', {
        component: 'CompanyInfoPage',
        action: 'useEffect',
        data: { source: 'companyInfo' },
      });
    } else if (state?.apiResponse) {
      // companyInfoがない場合はapiResponseから生成
      const generatedInfo = buildCompanyInfoFromUpload(state.apiResponse);
      setCompanyInfo({
        ...generatedInfo,
        criticalBusiness: generatedInfo.criticalBusiness || '重要事業1： \n重要事業2：',
      });

      logger.debug('Initialized from apiResponse', {
        component: 'CompanyInfoPage',
        action: 'useEffect',
        data: { source: 'apiResponse', generatedInfo },
      });
    } else {
      // どちらもない場合は空で初期化(手入力を許容)
      setCompanyInfo({
        companyName: '',
        location: '',
        employeeCount: undefined,
        businessDescription: '',
        criticalBusiness: '重要事業1： \n重要事業2：',
      });

      logger.info('No data provided, initialized with empty values', {
        component: 'CompanyInfoPage',
        action: 'useEffect',
      });
    }
  }, [state]);

  // 重要事業生成関数
  const handleGenerateCriticalBusiness = async () => {
    // 必須項目チェック
    if (!companyInfo.companyName || !companyInfo.businessDescription) {
      logger.debug('Skipping critical business generation: missing required fields', {
        component: 'CompanyInfoPage',
        hasCompanyName: !!companyInfo.companyName,
        hasBusinessDescription: !!companyInfo.businessDescription,
      });
      return;
    }

    // 既に重要事業が入力されている場合はスキップ（デフォルト値は除く）
    const defaultValue = '重要事業1： \n重要事業2：';
    if (companyInfo.criticalBusiness && companyInfo.criticalBusiness.trim() && companyInfo.criticalBusiness !== defaultValue) {
      logger.debug('Skipping critical business generation: already exists', {
        component: 'CompanyInfoPage',
      });
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    try {
      logger.info('Starting critical business generation', {
        component: 'CompanyInfoPage',
        companyName: companyInfo.companyName,
      });

      const result = await generateCriticalBusiness({
        company_name: companyInfo.companyName,
        location: companyInfo.location,
        employee_count: companyInfo.employeeCount,
        business_description: companyInfo.businessDescription,
      });

      if (result.success && result.critical_business) {
        setCompanyInfo(prev => ({
          ...prev,
          criticalBusiness: result.critical_business,
        }));

        logger.info('Critical business generated successfully', {
          component: 'CompanyInfoPage',
          resultLength: result.critical_business.length,
        });
      } else {
        throw new Error(result.error || '重要事業の生成に失敗しました');
      }
    } catch (error) {
      logger.error('Failed to generate critical business', {
        component: 'CompanyInfoPage',
        error: error instanceof Error ? error.message : String(error),
      });

      setGenerationError(
        error instanceof Error
          ? error.message
          : '重要事業の生成中にエラーが発生しました'
      );
    } finally {
      setIsGenerating(false);
      setHasAttemptedGeneration(true);
    }
  };

  // ページロード時に自動生成
  useEffect(() => {
    if (!hasAttemptedGeneration) {
      handleGenerateCriticalBusiness();
    }
  }, [companyInfo.companyName, companyInfo.businessDescription, hasAttemptedGeneration]);

  const handleInputChange = (
    field: keyof CompanyInfo,
    value: string | undefined
  ) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 従業員数入力用の特別なハンドラ
  const handleEmployeeCountChange = (value: string) => {
    // 全角数字を半角に変換
    const halfWidth = value.replace(/[０-９]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0xfee0)
    );

    // 単位(名、人)を除去
    const withoutUnit = halfWidth.replace(/[名人]/g, '').trim();

    // 数字のみまたは空文字列の場合のみ許可
    if (withoutUnit === '' || /^\d+$/.test(withoutUnit)) {
      setCompanyInfo(prev => ({
        ...prev,
        employeeCount: withoutUnit || undefined,
      }));
    }
  };

  const handleBack = () => {
    isNavigatingRef.current = true;
    navigation.goBack();
  };

  // 全項目が入力されているかチェック
  const isFormComplete = () => {
    return (
      companyInfo.companyName.trim() !== '' &&
      companyInfo.location.trim() !== '' &&
      companyInfo.employeeCount !== undefined &&
      companyInfo.employeeCount.trim() !== '' &&
      companyInfo.businessDescription.trim() !== '' &&
      companyInfo.criticalBusiness.trim() !== ''
    );
  };

  const handleNext = () => {
    isNavigatingRef.current = true;
    setError(null);

    // BCP作成ページへ遷移(既存データを全て引き継ぐ)
    // apiResponseに重要事業情報を追加
    const updatedApiResponse = state?.apiResponse ? {
      ...state.apiResponse,
      merged_extracted_info: {
        ...(state.apiResponse?.merged_extracted_info || state.apiResponse?.extracted_info || {}),
        company_name: companyInfo.companyName,
        address: companyInfo.location,
        business_content: companyInfo.businessDescription,
        main_business: companyInfo.criticalBusiness,
      },
    } : {
      merged_extracted_info: {
        company_name: companyInfo.companyName,
        address: companyInfo.location,
        business_content: companyInfo.businessDescription,
        main_business: companyInfo.criticalBusiness,
      },
    };

    navigation.navigateToBCPCreate({
      uploadedFiles: state?.uploadedFiles || [],
      apiResponse: updatedApiResponse,
      allUploadResults: state?.allUploadResults,
      bcpType: state?.bcpType || 'basic',
      companyInfo: {
        companyName: companyInfo.companyName,
        location: companyInfo.location,
        employeeCount: companyInfo.employeeCount,
        businessDescription: companyInfo.businessDescription,
        criticalBusiness: companyInfo.criticalBusiness,
      },
      bcpId: 'new',
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* ステップインジケーター */}
        <BCPStepIndicator currentStep={1} />

        {/* ページタイトル */}
        <div className="mt-8 mb-6">
          <h2 className="text-xl font-bold text-[#111827] mb-2">企業情報登録</h2>
          <p className="text-base text-[#4b5563] leading-6">
            BCP策定に必要な会社の基本情報を登録します。<br />
            これらの情報をもとに、貴社の事業内容に応じたBCPの下書きを自動生成します。<br />
            全項目の入力が必須となります。空欄や変更が必要な項目がある場合は、適宜編集してください。
          </p>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div className="mb-6">
            <Alert type="error">{error}</Alert>
          </div>
        )}

        {/* フォーム */}
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-8">
          {/* 会社名 */}
          <div>
            <label
              htmlFor="companyName"
              className="block text-sm font-medium text-[#0d357f] mb-2"
            >
              会社名<span className="text-red-600 ml-1">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              value={companyInfo.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              className="w-full px-3 py-3 border border-[#d1d5db] rounded-md focus:ring-2 focus:ring-[#0d357f] focus:border-[#0d357f] text-base"
              placeholder=""
            />
          </div>

          {/* 所在地 */}
          <div>
            <label
              htmlFor="location"
              className="block text-sm font-medium text-[#0d357f] mb-2"
            >
              所在地<span className="text-red-600 ml-1">*</span>
            </label>
            <input
              type="text"
              id="location"
              value={companyInfo.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-3 border border-[#d1d5db] rounded-md focus:ring-2 focus:ring-[#0d357f] focus:border-[#0d357f] text-base"
              placeholder=""
            />
          </div>

          {/* 従業員数 */}
          <div>
            <label
              htmlFor="employeeCount"
              className="block text-sm font-medium text-[#0d357f] mb-2"
            >
              従業員数<span className="text-red-600 ml-1">*</span>
            </label>
            <input
              type="text"
              id="employeeCount"
              value={companyInfo.employeeCount || ''}
              onChange={(e) => handleEmployeeCountChange(e.target.value)}
              className="w-full px-3 py-3 border border-[#d1d5db] rounded-md focus:ring-2 focus:ring-[#0d357f] focus:border-[#0d357f] text-base"
              placeholder=""
              inputMode="numeric"
            />
            <p className="mt-1 text-sm text-gray-500">
              数字のみを入力してください(半角・全角どちらでも可)
            </p>
          </div>

          {/* 事業内容 */}
          <div>
            <label
              htmlFor="businessDescription"
              className="block text-sm font-medium text-[#0d357f] mb-2"
            >
              事業内容<span className="text-red-600 ml-1">*</span>
            </label>
            <textarea
              id="businessDescription"
              value={companyInfo.businessDescription}
              onChange={(e) =>
                handleInputChange('businessDescription', e.target.value)
              }
              rows={1}
              className="w-full px-3 py-3 border border-[#d1d5db] rounded-md focus:ring-2 focus:ring-[#0d357f] focus:border-[#0d357f] text-base resize-none"
              placeholder=""
            />
          </div>

          {/* 重要事業 */}
          <div>
            <label
              htmlFor="criticalBusiness"
              className="block text-sm font-medium text-[#0d357f] mb-2"
            >
              重要事業<span className="text-red-600 ml-1">*</span>
              {isGenerating && (
                <span className="ml-2 text-sm text-gray-500">
                  <span className="inline-block animate-spin mr-1">⚙</span>
                  AIで生成中...
                </span>
              )}
            </label>
            {isGenerating ? (
              <div className="w-full h-24 bg-gray-100 rounded-md animate-pulse flex items-center justify-center">
                <p className="text-gray-500">重要事業を自動生成しています...</p>
              </div>
            ) : (
              <textarea
                id="criticalBusiness"
                value={companyInfo.criticalBusiness}
                onChange={(e) =>
                  handleInputChange('criticalBusiness', e.target.value)
                }
                rows={2}
                className="w-full px-3 py-3 border border-[#d1d5db] rounded-md focus:ring-2 focus:ring-[#0d357f] focus:border-[#0d357f] text-base resize-none"
              />
            )}
            {generationError && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  <strong>自動生成に失敗しました。</strong> 手動で入力してください。
                </p>
                <p className="text-xs text-yellow-600 mt-1">{generationError}</p>
              </div>
            )}
            <div className="mt-3 space-y-2" style={{ color: '#6B7280', fontSize: '14px', lineHeight: '20px', fontWeight: 350 }}>
              <p>・重要事業とは、災害等で事業が中断・停止した場合に、自社の存続を確保するため、経営資源を集中投入して優先的に継続・復旧させる事業のことです。</p>
              <p>・この項目で記載された重要事業は、以降のBCP下書き作成に活用されます。</p>
              <p>・重要事業は２つまで記載してください。重要事業特定の考え方や記載例は下記の「記入ガイドを表示」をご確認ください。</p>
            </div>
          </div>

          {/* ガイドセクション */}
          <BCPGuideSection
            guideContent={
              <div>
                <h5 className="font-medium mb-3" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>重要事業特定の考え方</h5>
                <p className="mb-2" style={{ fontSize: '14px', lineHeight: '20px' }}> 重要事業は、以下の観点を参考として、総合的に判断してください。<br />
                                    あまり深く考えすぎず、直感的な選定で問題ありません。
                </p>
                <ul className="list-disc pl-5 space-y-1" style={{ fontSize: '14px', lineHeight: '20px' }}>
                  <li>自社の売上への影響</li>
                  <li>顧客への迷惑</li>
                  <li>社会全体への迷惑</li>
                  <li>顧客離れの可能性</li>
                  <li>法的義務・契約上の責務</li>
                  <li>他の業務への影響</li>
                </ul>
              </div>
            }
            exampleContent={
              <div>
                <h5 className="font-medium mb-3" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>記載例</h5>
                <div className="border border-gray-300 rounded p-3 bg-white" style={{ fontSize: '14px', lineHeight: '20px', color: '#4B5563' }}>
                  <div>
                    <p className="font-bold">■製造業の場合:</p>
                    <div className="ml-4">
                      <p>(例1) Ａ製品群の製造、Ｂ製品群の製造</p>
                      <p>(例2) 医療機関向けの製品の製造、Ａ社向けの製品の製造</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="font-bold">■小売業の場合:</p>
                    <div className="ml-4">
                      <p>(例1) 食料品、日用雑貨品</p>
                      <p>(例2) 通販事業、スーパー店舗販売</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="font-bold">■サービス業の場合:</p>
                    <div className="ml-4">
                      <p>(例1) Ａサービス、Ｂサービス</p>
                      <p>(例2) 病院向けサービス、学校向けサービス</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="font-bold">■建設業、土木業、建設設計業、不動産業の場合:</p>
                    <div className="ml-4">
                      <p>(例1) 最重要顧客(Ａ社)対応、重要顧客(Ｂ社、Ｃ社)対応</p>
                      <p>(例2) 施工中物件の工事再開、施工済み物件の点検・修理</p>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>

        {/* ボタンエリア */}
        <div className="mt-8 flex justify-between border-t border-[#e5e7eb] pt-6">
          <button
            onClick={handleBack}
            className="px-6 py-2 border border-[#d1d5db] rounded-md text-[#0d357f] font-medium hover:bg-gray-50"
          >
            戻る
          </button>
          <button
            onClick={handleNext}
            disabled={!isFormComplete()}
            className={`px-8 py-2 rounded-md font-medium transition-colors ${
              isFormComplete()
                ? 'bg-[#0d357f] text-white hover:bg-[#0a2a64] cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            次へ進む
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfoPage;
